import { useState } from "react"

import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"

import { useStepSave } from "./nav"
import { OptionCard } from "./option-card"
import type {
  CriticismHandling,
  PraiseHandling,
  ReviewResponderStepProps,
} from "./types"

const CRITICISM_OPTIONS: {
  value: CriticismHandling
  label: string
  description: string
  example: string
}[] = [
  {
    value: "make-it-right",
    label: "Offer to make it right",
    description:
      "Acknowledge the issue and invite the reviewer to get in touch.",
    example:
      "We're sorry your visit didn't meet expectations — that's not the experience we want for you. We'd love to make it right; please reach out so we can help.",
  },
  {
    value: "acknowledge",
    label: "Acknowledge without committing",
    description: "Validate and express regret, without promising a resolution.",
    example:
      "Thank you for the honest feedback. We're sorry to hear this fell short, and we'll share your comments with the team as we keep improving.",
  },
]

const PRAISE_OPTIONS: {
  value: PraiseHandling
  label: string
  description: string
  example: string
}[] = [
  {
    value: "specific",
    label: "Specific and personal",
    description: "Pick up on details in the review and weave them in.",
    example:
      "Thank you, Rachel! We're so glad the team got everything sorted quickly for you — we'll pass your kind words along. Can't wait to welcome you back!",
  },
  {
    value: "short",
    label: "Short and gracious",
    description: "A brief, genuine thank-you. Reads well when volume is high.",
    example: "Thank you so much for the kind words — we really appreciate it!",
  },
]

function readCriticism(data: Record<string, unknown>): CriticismHandling {
  const v = data.criticism_handling
  return CRITICISM_OPTIONS.some((o) => o.value === v)
    ? (v as CriticismHandling)
    : "make-it-right"
}

function readPraise(data: Record<string, unknown>): PraiseHandling {
  const v = data.praise_handling
  return PRAISE_OPTIONS.some((o) => o.value === v)
    ? (v as PraiseHandling)
    : "specific"
}

function ExampleBox({ text }: { text: string }) {
  return (
    <div className="bg-muted/60 rounded-lg border p-4">
      <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
        Example
      </p>
      <p className="text-sm leading-relaxed">&ldquo;{text}&rdquo;</p>
    </div>
  )
}

function GuardrailsSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>You stay in control</SheetTitle>
          <SheetDescription>
            Some protections are built into every response and can&rsquo;t be
            overridden by your settings, your instructions, or the review
            itself.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-6 px-4 pb-6 text-sm">
          <section className="space-y-2">
            <p className="font-semibold">Built-in guardrails</p>
            <ul className="text-muted-foreground list-disc space-y-1 pl-4">
              <li>Never invents facts, offers, or commitments.</li>
              <li>No legal, medical, or financial advice.</li>
              <li>Never shares personal or private customer information.</li>
              <li>Stays professional even when a review is hostile.</li>
            </ul>
          </section>
          <section className="space-y-2">
            <p className="font-semibold">Always flagged for your approval</p>
            <p className="text-muted-foreground">
              The agent still drafts a careful reply, but never posts these on
              its own — they wait for you to review:
            </p>
            <ul className="text-muted-foreground list-disc space-y-1 pl-4">
              <li>Legal threats or mentions of lawyers.</li>
              <li>Health, safety, or injury claims.</li>
              <li>Allegations of discrimination or harassment.</li>
              <li>Anything matching your custom escalation terms.</li>
            </ul>
          </section>
          <section className="space-y-2">
            <p className="font-semibold">The controls you keep</p>
            <ul className="text-muted-foreground list-disc space-y-1 pl-4">
              <li>Draft mode — nothing posts until you approve it.</li>
              <li>Pause everything with one click, any time.</li>
              <li>Edit any draft before it goes out.</li>
            </ul>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export function BehaviorStep({
  agent,
  stepNumber,
  totalSteps,
  exampleSet,
}: ReviewResponderStepProps) {
  const { processing, save } = useStepSave(agent.uid)
  const [guardrailsOpen, setGuardrailsOpen] = useState(false)
  const [criticism, setCriticism] = useState<CriticismHandling>(() =>
    readCriticism(agent.setup_data),
  )
  const [praise, setPraise] = useState<PraiseHandling>(() =>
    readPraise(agent.setup_data),
  )

  const data = {
    criticism_handling: criticism,
    praise_handling: praise,
  }

  const criticismExample = CRITICISM_OPTIONS.find((o) => o.value === criticism)
  const praiseExample = PRAISE_OPTIONS.find((o) => o.value === praise)
  // IMP-305: industry-themed examples when available, else the general ones.
  const criticismText =
    (criticism === "make-it-right"
      ? exampleSet?.criticism.make_it_right
      : exampleSet?.criticism.acknowledge) ?? criticismExample?.example
  const praiseText =
    (praise === "specific"
      ? exampleSet?.praise.specific
      : exampleSet?.praise.short) ?? praiseExample?.example

  return (
    <WizardLayout
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => save("step-tone", data)}
            disabled={processing}
          >
            Back
          </Button>
          <Button
            onClick={() => save("step-customization", data)}
            disabled={processing}
          >
            Continue
          </Button>
        </>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Response behavior"
        description={
          <>
            Configure what the agent says for different types of reviews.
            Guardrails apply to every response.{" "}
            <button
              type="button"
              onClick={() => setGuardrailsOpen(true)}
              className="underline underline-offset-2 hover:no-underline"
            >
              See the guardrails.
            </button>
          </>
        }
      />

      <GuardrailsSheet open={guardrailsOpen} onOpenChange={setGuardrailsOpen} />

      <div className="space-y-3">
        <Label>Criticism</Label>
        <div className="grid grid-cols-2 gap-2">
          {CRITICISM_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              selected={criticism === opt.value}
              onClick={() => setCriticism(opt.value)}
              title={opt.label}
              description={opt.description}
            />
          ))}
        </div>
        {criticismText && <ExampleBox text={criticismText} />}
      </div>

      <div className="space-y-3">
        <Label>Praise</Label>
        <div className="grid grid-cols-2 gap-2">
          {PRAISE_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              selected={praise === opt.value}
              onClick={() => setPraise(opt.value)}
              title={opt.label}
              description={opt.description}
            />
          ))}
        </div>
        {praiseText && <ExampleBox text={praiseText} />}
      </div>
    </WizardLayout>
  )
}
